<script setup lang="ts">
import { ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import PersonAvatar from "@/components/People/PersonAvatar.vue";

interface Props {
  tableCaption: string;
  students?: any[];
  teachers?: any[];
  onClickRouteName?: string;
  onClickRouteParams?: Record<string, any>;
  disabledPeopleIds?: string[];
  isPeopleSelectable?: boolean;
  isPeopleClickable?: boolean;
  showGroups?: boolean;
  selectedPeople?: any[];
  emphasizePersonKey?: string | null;
  emphasizePersonLabel?: string | null;
  column2SortKey?: string;
  column3SortKey?: string;
  column4SortKey?: string;
  column5SortKey?: string;
  column6SortKey?: string;
  defaultSortKey?: string;
  defaultSortDirection?: string;
}

const props = withDefaults(defineProps<Props>(), {
  students: () => undefined,
  teachers: () => undefined,
  onClickRouteName: "home",
  onClickRouteParams: () => ({}),
  disabledPeopleIds: () => [],
  isPeopleSelectable: false,
  isPeopleClickable: false,
  showGroups: true,
  selectedPeople: () => [],
  emphasizePersonKey: null,
  emphasizePersonLabel: null,
  column2SortKey: undefined,
  column3SortKey: undefined,
  column4SortKey: undefined,
  column5SortKey: undefined,
  column6SortKey: undefined,
  defaultSortKey: "name",
  defaultSortDirection: "asc"
});

const emit = defineEmits<{
  (e: "selectedPeopleChanged", value: any[]): void;
  (e: "orderByDirectionChanged", value: string): void;
  (e: "orderByPropertyChanged", value: string): void;
  (e: "personClick", value: any): void;
}>();

const { t } = useI18n();

const orderByProperty = ref(props.defaultSortKey);
const orderByDirection = ref(props.defaultSortDirection);
const lastSelectedPersonIndex = ref(0);

const isPersonDisabled = (person: any) =>
  props.disabledPeopleIds && props.disabledPeopleIds.indexOf(person?.userId) > -1;

const isPersonEmphasized = (person: any) => props.emphasizePersonKey && person[props.emphasizePersonKey];

const allVisiblePeople = computed(() =>
  sortedGroups.value.flatMap((g: any) => g.people).filter((p: any) => !isPersonDisabled(p))
);

const allPeopleSelected = computed(
  () =>
    allVisiblePeople.value.length > 0 &&
    allVisiblePeople.value.every((p: any) => props.selectedPeople.some((sp: any) => sp.userId === p.userId))
);

const somePeopleSelected = computed(() => props.selectedPeople.length > 0);

const toggleSelectAll = () => {
  emit("selectedPeopleChanged", allPeopleSelected.value ? [] : [...allVisiblePeople.value]);
};

const sortPeople = (people: any[]) => {
  const selectedIds = new Set(props.selectedPeople.map((sp: any) => sp.userId));
  const sortedList = people.map(p => ({ ...p, selected: selectedIds.has(p.userId) }));
  if (orderByProperty.value === "name") {
    sortedList.sort((a: any, b: any) => (a["name"]["name"] < b["name"]["name"] ? -1 : 1));
    if (orderByDirection.value === "desc") sortedList.reverse();
  } else {
    sortedList.sort((a: any, b: any) => {
      if (a[orderByProperty.value] == b[orderByProperty.value]) {
        return a["name"]["name"] < b["name"]["name"] ? -1 : 1;
      }
      if (orderByDirection.value === "desc") {
        return a[orderByProperty.value] > b[orderByProperty.value] ? -1 : 1;
      }
      return a[orderByProperty.value] < b[orderByProperty.value] ? -1 : 1;
    });
  }
  return sortedList;
};

const sortedGroups = computed(() => {
  if (!props.students && !props.teachers) {
    return null;
  }

  const groups: any[] = [];
  if (props.teachers?.length > 0) {
    groups.push({ role: t("People.Roles.Teachers"), people: sortPeople(props.teachers) });
  }
  if (props.students?.length > 0) {
    groups.push({ role: t("People.Roles.Students"), people: sortPeople(props.students) });
  }
  if (orderByDirection.value === "desc") groups.reverse();
  return groups;
});

const onClick = (person: any) => {
  if (isPersonDisabled(person)) return;
  emit("personClick", person);
};

const select = (person: any, personIndex: number, group?: any, modifier?: string) => {
  if (!props.isPeopleSelectable) {
    onClick(person);
  }

  if (isPersonDisabled(person)) return;

  let selectedPeopleCp = [...props.selectedPeople];

  if (person.selected) {
    if (selectedPeopleCp.length > 1) {
      if (modifier === "ctrl") {
        const indexOfSelectedPerson = selectedPeopleCp.findIndex((p: any) => p.userId === person.userId);
        selectedPeopleCp.splice(indexOfSelectedPerson, 1);
      } else {
        selectedPeopleCp = [person];
      }
    } else {
      selectedPeopleCp = [];
    }
  } else {
    if (modifier !== "shift" && modifier !== "ctrl") {
      selectedPeopleCp = [];
    }

    if (modifier === "shift" && selectedPeopleCp.length > 0) {
      if (personIndex > lastSelectedPersonIndex.value) {
        selectedPeopleCp.push(...group.people.slice(lastSelectedPersonIndex.value + 1, personIndex));
      } else {
        selectedPeopleCp.push(...group.people.slice(personIndex + 1, lastSelectedPersonIndex.value));
      }
    }
    selectedPeopleCp.push(person);
  }

  lastSelectedPersonIndex.value = personIndex;
  emit("selectedPeopleChanged", selectedPeopleCp);
};

const setSortProperties = (evnt: any) => {
  if (evnt.target.id) {
    if (orderByProperty.value === evnt.target.id) {
      orderByDirection.value = orderByDirection.value === "asc" ? "desc" : "asc";
      emit("orderByDirectionChanged", orderByDirection.value);
    } else {
      orderByProperty.value = evnt.target.id;
      orderByDirection.value = "asc";
      emit("orderByPropertyChanged", orderByProperty.value);
      emit("orderByDirectionChanged", orderByDirection.value);
    }
  } else if (evnt.target.parentElement.id) {
    if (orderByProperty.value === evnt.target.parentElement.id) {
      orderByDirection.value = orderByDirection.value === "asc" ? "desc" : "asc";
      emit("orderByDirectionChanged", orderByDirection.value);
    } else {
      orderByProperty.value = evnt.target.parentElement.id;
      orderByDirection.value = "asc";
      emit("orderByDirectionChanged", orderByDirection.value);
      emit("orderByPropertyChanged", orderByProperty.value);
    }
  } else {
    // eslint-disable-next-line no-console
    console.error("No id on sort elm");
  }
};

const calculateSortDirectionForColumn = (columnName: string) => {
  if (orderByProperty.value === columnName) {
    return orderByDirection.value === "asc" ? "ascending" : "descending";
  }
  return undefined;
};

const calcRouteParams = (person: any) => {
  const paramsList = Object.keys(props.onClickRouteParams);
  const newParamsList: Record<string, any> = {};
  paramsList.forEach(p => {
    if (typeof props.onClickRouteParams[p] === "string" && props.onClickRouteParams[p].startsWith(":")) {
      const propertyNameOnPersonObject = props.onClickRouteParams[p].substring(1);
      newParamsList[p] = person[propertyNameOnPersonObject];
    } else {
      newParamsList[p] = props.onClickRouteParams[p];
    }
  });
  return { ...newParamsList };
};
</script>

<template>
  <table class="w-full">
    <caption>
      {{
        tableCaption
      }}
      ({{
        $t("Tooltips.ColumnHeadersWithButtonsAreSortable")
      }})
    </caption>
    <thead class="bg-gray-100">
      <tr class="border-b-2 border-gray-200" role="row">
        <th v-if="isPeopleSelectable" class="text-left w-2" role="columnheader" scope="col">
          <label v-if="sortedGroups" class="nct-checkmark" :aria-label="$t('Common.SelectAll')" @click.stop>
            <input
              type="checkbox"
              :checked="allPeopleSelected"
              :indeterminate.prop="somePeopleSelected && !allPeopleSelected"
              @click.stop="toggleSelectAll"
            />
            <span></span>
          </label>
        </th>
        <th
          class="h-14 pl-2 text-left w-auto"
          colspan="2"
          role="columnheader"
          scope="col"
          :aria-sort="calculateSortDirectionForColumn('name')"
        >
          <button
            id="name"
            class="flex items-center font-semibold"
            :aria-pressed="!!calculateSortDirectionForColumn('name')"
            @click="setSortProperties"
          >
            {{ $t("name") }}
            <span class="ml-2" :css-arrow="orderByDirection" aria-hidden="true"></span>
          </button>
        </th>

        <th
          v-if="$slots.column2Header"
          class="p-2 text-left hidden md:table-cell"
          role="columnheader"
          scope="col"
          :aria-sort="calculateSortDirectionForColumn(column2SortKey)"
        >
          <span v-if="!column2SortKey">
            <slot name="column2Header"></slot>
          </span>
          <button
            v-else
            :id="column2SortKey"
            class="flex items-center font-semibold"
            :aria-pressed="!!calculateSortDirectionForColumn('column2SortKey')"
            @click="setSortProperties"
          >
            <slot name="column2Header"></slot>
            <span class="ml-2" :css-arrow="orderByDirection" aria-hidden="true"></span>
          </button>
        </th>

        <th
          v-if="$slots.column3Header"
          class="p-2 hidden md:table-cell"
          role="columnheader"
          scope="col"
          :aria-sort="calculateSortDirectionForColumn(column3SortKey)"
        >
          <span v-if="!column3SortKey">
            <slot name="column3Header"></slot>
          </span>
          <button
            v-else
            :id="column3SortKey"
            class="flex items-center font-semibold"
            :aria-pressed="!!calculateSortDirectionForColumn('column3SortKey')"
            @click="setSortProperties"
          >
            <slot name="column3Header"></slot>
            <span class="ml-2" :css-arrow="orderByDirection" aria-hidden="true"></span>
          </button>
        </th>

        <th
          v-if="$slots.column4Header"
          class="p-2 hidden md:table-cell text-left"
          role="columnheader"
          scope="col"
          :aria-sort="calculateSortDirectionForColumn(column4SortKey)"
        >
          <span v-if="!column4SortKey">
            <slot name="column4Header"></slot>
          </span>
          <button
            v-else
            :id="column4SortKey"
            class="flex items-center font-semibold"
            :aria-pressed="!!calculateSortDirectionForColumn('column4SortKey')"
            @click="setSortProperties"
          >
            <slot name="column4Header"></slot>
            <span class="ml-2" :css-arrow="orderByDirection" aria-hidden="true"></span>
          </button>
        </th>

        <th
          v-if="$slots.column5Header"
          class="p-2 hidden md:table-cell text-left"
          role="columnheader"
          scope="col"
          :aria-sort="calculateSortDirectionForColumn(column5SortKey)"
        >
          <span v-if="!column5SortKey">
            <slot name="column5Header"></slot>
          </span>
          <button
            v-else
            :id="column5SortKey"
            class="flex items-center font-semibold"
            :aria-pressed="!!calculateSortDirectionForColumn('column5SortKey')"
            @click="setSortProperties"
          >
            <slot name="column5Header"></slot>
            <span class="ml-2" :css-arrow="orderByDirection" aria-hidden="true"></span>
          </button>
        </th>

        <th
          v-if="$slots.column6Header"
          class="p-2 hidden md:table-cell text-left w-auto"
          role="columnheader"
          scope="col"
          :aria-sort="calculateSortDirectionForColumn(column6SortKey)"
        >
          <span v-if="!column6SortKey">
            <slot name="column6Header"></slot>
          </span>
          <button
            v-else
            :id="column6SortKey"
            class="flex items-center font-semibold"
            :aria-pressed="!!calculateSortDirectionForColumn('column6SortKey')"
            @click="setSortProperties"
          >
            <slot name="column6Header"></slot>
            <span class="ml-2" :css-arrow="orderByDirection" aria-hidden="true"></span>
          </button>
        </th>
      </tr>
    </thead>
    <tbody v-for="(group, groupIndex) in sortedGroups" :key="groupIndex">
      <tr v-if="showGroups" class="bg-gray-150" role="row">
        <th v-if="isPeopleSelectable" colspan="2"></th>
        <th colspan="2" class="p-2 text-left font-normal uppercase text-sm">{{ group.role }}</th>
      </tr>
      <tr
        v-for="(person, key, personIndex) in group.people"
        :key="person.userId"
        role="row"
        class="border-b last:border-0 border-gray-200 hover:bg-gray-150"
        :class="{
          'cursor-default': (!isPeopleSelectable && !isPeopleClickable) || isPersonDisabled(person),
          'opacity-60': isPersonDisabled(person),
          'text-danger font-extrabold': isPersonEmphasized(person),
          'cursor-pointer': isPeopleSelectable || (isPeopleClickable && !isPersonDisabled(person))
        }"
        style="user-select: none"
        :data-selected="person.selected && isPeopleSelectable"
        :title="isPersonEmphasized(person) ? emphasizePersonLabel : person.name.name"
        :disabled="isPersonDisabled(person)"
        @click.exact="select(person, personIndex, group, 'ctrl')"
        @click.ctrl.stop.prevent="select(person, personIndex, group, 'ctrl')"
        @click.shift.stop.prevent="select(person, personIndex, group, 'shift')"
      >
        <td v-if="isPeopleSelectable">
          <label class="nct-checkmark" :aria-label="$t('Common.Select', { name: person.name.name })" @click.stop>
            <input
              type="checkbox"
              :checked="person.selected"
              :disabled="isPersonDisabled(person)"
              @click.stop="select(person, personIndex, group, 'ctrl')"
            />
            <span></span>
          </label>
        </td>
        <td class="p-2">
          <PersonAvatar :person="person" :tooltip="false" />
        </td>
        <td class="p-2 pl-2 w-full">
          <router-link
            v-if="isPeopleClickable && calcRouteParams(person)"
            :to="{ name: onClickRouteName, params: calcRouteParams(person) }"
            >{{ person.name.name }}
          </router-link>
          <span v-else> {{ person.name.name }} </span>

          <div class="md:hidden">
            <div class="flex">
              <div class="text-xs text-gray-500">
                <slot name="column2Content" :person="person"></slot>
              </div>
              <div class="text-xs text-gray-500 ml-4">
                <slot name="column3Content" :person="person"></slot>
              </div>
            </div>
            <div class="text-xs text-gray-500">
              <slot name="column4Content" :person="person"></slot>
            </div>
          </div>
        </td>

        <td v-if="$slots.column2Content" class="p-2 pr-1 hidden md:table-cell">
          <slot name="column2Content" :person="person"></slot>
        </td>

        <td v-if="$slots.column3Content" class="p-2 pr-1 text-center hidden md:table-cell">
          <slot name="column3Content" :person="person"></slot>
        </td>

        <td v-if="$slots.column4Content" class="p-2 pr-1 hidden md:table-cell">
          <slot name="column4Content" :person="person"></slot>
        </td>

        <td v-if="$slots.column5Content" class="p-2 pr-1 hidden md:table-cell">
          <slot name="column5Content" :person="person"></slot>
        </td>

        <td v-if="$slots.column6Content" class="p-2 pr-1 hidden md:table-cell w-auto">
          <slot name="column6Content" :person="person"></slot>
        </td>
      </tr>
    </tbody>
  </table>
</template>

<style scoped>
/* Collapse label's layout width so the column stays at w-2 (set on <th>).
   The visible circle is position:absolute inside the label, so it still renders. */
.nct-checkmark {
  width: 0;
}
</style>
